import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { Text, Title } from "@src/appV2/redesign/system/ui-components";
import { useMemo } from "react";

import { OpenShiftCardContainer } from "../../Shift/Open/CardContainer";
import type { Offer, OpenShift, OpenShiftWorkplace } from "../../Shift/Open/types";

interface FallbackShiftsListProps {
  shifts: OpenShift[];
  totalShiftsCount: number;
  workplacesMap: Map<string, OpenShiftWorkplace>;
  onClickOpenShift: ({
    shift,
    workplace,
    offer,
  }: {
    shift: OpenShift;
    workplace: OpenShiftWorkplace;
    offer?: Offer;
  }) => void;
}

/**
 * Displays up to 5 unfiltered shifts when filters are applied but return fewer
 * than 15 shifts. `shifts` is already deduplicated against the main list, so
 * this renders only shifts the user cannot currently see. `totalShiftsCount`
 * is the raw unfiltered total used in the "Clear your filters to see all N
 * shifts" label.
 */
export function FallbackShiftsList(props: FallbackShiftsListProps) {
  const { shifts, totalShiftsCount, workplacesMap, onClickOpenShift } = props;

  // Take only the first 5
  const limitedShifts = useMemo(() => shifts.slice(0, 5), [shifts]);

  return (
    <Stack spacing={5} sx={{ py: 5 }}>
      <Title semibold variant="h5" component="h3">
        Shifts outside your filters
      </Title>

      {limitedShifts.map((shift) => {
        const workplace = workplacesMap.get(shift.relationships.workplace.data.id);
        if (!isDefined(workplace)) {
          return null;
        }

        return (
          <OpenShiftCardContainer
            key={shift.id}
            shift={shift}
            workplace={workplace}
            blockingRequirementsContext={undefined}
            workers={[]}
            friendsMap={new Map()}
            recentColleaguesMap={new Map()}
            onClick={({ offer }) => {
              onClickOpenShift({ shift, workplace, offer });
            }}
          />
        );
      })}

      <Text
        semibold
        variant="body2"
        color={(theme) => theme.palette.text.tertiary}
        sx={{ textAlign: "center" }}
      >
        Clear your filters to see all {totalShiftsCount} shifts
      </Text>
    </Stack>
  );
}
